import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatBoolean } from '@utils/formats.ts';
import { DayOfWeek } from '@api/generated/search-service';

interface ProductInfoProps {
  details: {
    ecid: string;
    supplier: string;
    minAmount: number;
    onlyMultiple: boolean;
    catalogNumber: string;
    catalogName: string;
    auctionId: string;
    description: string;
    deliveryDays: Array<DayOfWeek>;
  };
  /**
   * Když je true, schovat všechna pole vázaná na dodavatele (supplier, deliveryDays,
   * catalogNumber, catalogName, description). Použito v empty-state pro datum dodání,
   * kde N/A pole vypadají divně.
   */
  compact?: boolean;
}

const ProductInfo: React.FC<ProductInfoProps> = ({ details, compact = false }) => {
  const { t } = useTranslation('client');
  return (
    <div className="w-full flex flex-col justify-start items-start gap-6">
      {/* Základní informace */}
      <div className="w-full flex flex-wrap justify-start items-start gap-4">
        <div className="h-[50px] p-2 bg-gray-50 rounded inline-flex flex-col justify-start items-center gap-2">
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{t('productDetail.minOrder')}</div>
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{details.minAmount}</div>
        </div>
        <div className="h-[50px] p-2 bg-gray-50 rounded inline-flex flex-col justify-start items-center gap-2">
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{t('productDetail.multiples')}</div>
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{formatBoolean(details.onlyMultiple)}</div>
        </div>
        {/*<AllergensOverlay allergens={basicInfo.allergens} allergenList={allergenList} />*/}
      </div>

      {/* Detailní informace o produktu */}
      <div className="w-full flex flex-col justify-start items-start gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-1 sm:gap-3">
          <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.ecid')}</div>
          <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.ecid}</div>

          {!compact && (
            <>
              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.supplierLabel')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.supplier}</div>
              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.deliveryDays')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.deliveryDays?.join(', ')}</div>

              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.catalogNumber')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.catalogNumber}</div>
            </>
          )}
        </div>
        {!compact && (
          <div className={'w-full flex flex-col justify-start items-start gap-3'}>
            <div className="w-full flex flex-col justify-start items-start gap-1 mt-4">
              <div className="w-full sm:w-[130px] text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.catalogName')}</div>
              <div className="w-full text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.catalogName}</div>
            </div>

            <div className="w-full flex flex-col justify-start items-start gap-1">
              <div className="w-full sm:w-[130px] text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.productDescription')}</div>
              <div className="w-full text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.description}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;
